import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import * as Sentry from "@sentry/react";
import { AuthCognitoPool, configureAmplifyPool } from "../../utils/amplifyPools";

type ForgotPasswordProps = {
  cognitoPool?: AuthCognitoPool;
};

export default function ForgotPassword({ cognitoPool = "member" }: ForgotPasswordProps) {
  const location = useLocation();
  const navigate = useNavigate();
  //@ts-ignore
  const { email: locationEmail } = location.state ? location.state : {};
  const [email, setEmail] = useState(locationEmail || "");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    try {
      configureAmplifyPool(cognitoPool);
    } catch (e) {
      console.error(e);
    }
  }, [cognitoPool]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = email.trim();
    
    try {
      setIsLoading(true);
      await Auth.forgotPassword(username);
      toast.success("Reset code is mailed to your Email Id");
      setIsLoading(false);
      const resetPath = cognitoPool === "measureOperator" ? "/measure/reset-password" : "/reset-password";
      navigate(resetPath, {
        state: { username: username, cognitoPool },
      });
    } catch (err: any) {
      Sentry.captureException(err);
      setIsLoading(false);
      console.error(err);
      let errorMessage = "Something is wrong please try later";
      if (err.code === "UserNotFoundException") errorMessage = "User not found";
      if (err.code === "InvalidParameterException")
        errorMessage =
          "Cannot reset password for the user as there is no verified email, confirm the user and try again";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 ">
            <Link to="/" className=" mb-8">
              <img
                src={measureLogo}
                alt="Measure Ignite"
                className="h-25 w-auto mx-auto"
              />
            </Link>
            <h1 className="text-3xl font-semibold mb-2">
              Reset your password
            </h1>
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you
              instructions to reset your password
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={isLoading}
            >
              {isLoading
                ? "Sending..."
                : "Send Reset Instructions"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to={cognitoPool === "measureOperator" ? "/measure/login" : "/login"}
              className="text-sm text-gray-600 hover:text-orange-600 flex items-center justify-center gap-1"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to sign in
            </Link>
          </div>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              We've got you covered
            </h2>
            <p className="text-gray-700 text-lg">
              Forgot your password? No problem. We'll help you
              get back into your account in no time.
            </p>
          </div>
          <FourDots size="2xl" opacity={0.7} />
        </div>
      </div>
    </div>
  );
}
